import { useState, type KeyboardEvent } from 'react';
import { Box, Typography } from '@mui/material';

import { ANALYTICS } from './analyticsTokens';
import { TREND_PLOT, TREND_SIZE, endLabels, labelIndexes, linePath, trendScale, xOf, yOf, type Trend } from './betaTrendGeometry';
import { formatPeriod, formatPeriodShort, type PeriodUnit } from './betaUsageRanges';

const base = { fontFamily: ANALYTICS.font } as const;

const cardSx = {
  bgcolor: ANALYTICS.surface,
  borderRadius: '8px',
  border: `1px solid ${ANALYTICS.grey50}`,
  padding: '24px',
} as const;

const axisText = { fontFamily: ANALYTICS.font, fontSize: 12, fill: ANALYTICS.disabled, fontVariantNumeric: 'tabular-nums' } as const;

export function BetaUsageTrend({ trend, unit }: { trend: Trend; unit: PeriodUnit }) {
  return (
    <Box data-testid="beta-usage-trend" component="section" aria-label="People active" sx={{ ...cardSx, display: 'flex', flexDirection: 'column', gap: '24px' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '16px', flexWrap: 'wrap' }}>
        <Typography
          component="h2"
          data-testid="beta-usage-trend-title"
          sx={{ ...base, fontSize: 20, lineHeight: '28px', fontWeight: 700, color: ANALYTICS.ink }}
        >
          {unit === 'day' ? 'People active, by day' : 'People active, by week'}
        </Typography>
        <Box data-testid="beta-usage-trend-legend" sx={{ display: 'flex', flexWrap: 'wrap', gap: '20px' }}>
          {trend.series.map((line) => (
            <Box
              key={line.feature.id}
              data-testid="beta-usage-trend-legend-item"
              sx={{ ...base, display: 'flex', alignItems: 'center', gap: '8px', fontSize: 12, lineHeight: '16px', fontWeight: 600, color: ANALYTICS.muted }}
            >
              <Box component="span" aria-hidden="true" sx={{ width: 16, height: 4, borderRadius: '9999px', bgcolor: line.color, flexShrink: 0 }} />
              {line.feature.name}
            </Box>
          ))}
        </Box>
      </Box>
      <TrendChart trend={trend} unit={unit} />
    </Box>
  );
}

function TrendChart({ trend, unit }: { trend: Trend; unit: PeriodUnit }) {
  const [active, setActive] = useState<number | null>(null);
  const count = trend.periods.length;
  const last = count - 1;
  const scale = trendScale(Math.max(0, ...trend.series.flatMap((line) => line.values)));
  const step = count > 1 ? (TREND_PLOT.right - TREND_PLOT.left) / (count - 1) : TREND_PLOT.right - TREND_PLOT.left;

  const onKeyDown = (event: KeyboardEvent<SVGSVGElement>) => {
    const from = active ?? last;
    const to = { ArrowLeft: Math.max(0, from - 1), ArrowRight: Math.min(last, from + 1), Home: 0, End: last }[event.key];
    if (to === undefined) return;
    event.preventDefault();
    setActive(to);
  };

  return (
    <Box sx={{ position: 'relative' }}>
      <Box
        component="svg"
        data-testid="beta-usage-trend-chart"
        role="img"
        tabIndex={0}
        aria-label={`People active per ${unit}, by feature, from ${formatPeriod(trend.periods[0] ?? '', unit)} to ${formatPeriod(trend.periods[last] ?? '', unit)}.`}
        viewBox={`0 0 ${TREND_SIZE.width} ${TREND_SIZE.height}`}
        onFocus={() => setActive((current) => current ?? last)}
        onBlur={() => setActive(null)}
        onPointerLeave={() => setActive(null)}
        onKeyDown={onKeyDown}
        sx={{ display: 'block', width: '100%', height: 'auto', overflow: 'visible', outlineOffset: '4px' }}
      >
        {scale.ticks.map((tick) => (
          <g key={tick}>
            <line
              x1={TREND_PLOT.left}
              x2={TREND_PLOT.right}
              y1={yOf(tick, scale.top)}
              y2={yOf(tick, scale.top)}
              stroke={tick === 0 ? ANALYTICS.grey100 : ANALYTICS.grey50}
              strokeWidth={1}
            />
            <text x={0} y={yOf(tick, scale.top) + 4} style={axisText}>
              {tick}
            </text>
          </g>
        ))}
        {labelIndexes(count).map((index) => (
          <text key={index} x={xOf(index, count)} y={TREND_SIZE.height - 8} textAnchor="middle" style={axisText}>
            {formatPeriodShort(trend.periods[index] ?? '', unit, count)}
          </text>
        ))}
        {active !== null && (
          <line x1={xOf(active, count)} x2={xOf(active, count)} y1={TREND_PLOT.top} y2={TREND_PLOT.bottom} stroke={ANALYTICS.grey100} strokeWidth={1} />
        )}
        {[...trend.series].reverse().map((line) => (
          <g key={line.feature.id}>
            <path
              data-testid="beta-usage-trend-line"
              data-feature={line.feature.id}
              d={linePath(line.values, scale.top)}
              fill="none"
              stroke={line.color}
              strokeWidth={2.5}
              strokeLinejoin="round"
              strokeLinecap="round"
            />
            {active !== null && line.values[active] !== undefined && (
              <circle cx={xOf(active, count)} cy={yOf(line.values[active] ?? 0, scale.top)} r={4} fill={line.color} stroke={ANALYTICS.surface} strokeWidth={2} />
            )}
          </g>
        ))}
        {endLabels(trend.series, scale.top).map((label) => (
          <text
            key={label.text}
            data-testid="beta-usage-trend-end-label"
            x={label.x + 12}
            y={label.y + 4}
            style={{ fontFamily: ANALYTICS.font, fontSize: 12, fontWeight: 700, fill: ANALYTICS.ink, fontVariantNumeric: 'tabular-nums' }}
          >
            {label.text}
          </text>
        ))}
        {trend.periods.map((period, index) => (
          <rect
            key={period}
            data-testid="beta-usage-trend-hit"
            x={xOf(index, count) - step / 2}
            y={0}
            width={step}
            height={TREND_SIZE.height}
            fill="transparent"
            onPointerEnter={() => setActive(index)}
          />
        ))}
      </Box>
      {active !== null && (
        <Box
          data-testid="beta-usage-trend-tooltip"
          role="status"
          sx={{
            ...base,
            position: 'absolute',
            top: 0,
            left: `${(xOf(active, count) / TREND_SIZE.width) * 100}%`,
            transform: active > last / 2 ? 'translateX(calc(-100% - 12px))' : 'translateX(12px)',
            pointerEvents: 'none',
            bgcolor: ANALYTICS.surface,
            border: `1px solid ${ANALYTICS.grey50}`,
            borderRadius: '8px',
            boxShadow: ANALYTICS.elevationCard,
            padding: '8px 12px',
            whiteSpace: 'nowrap',
          }}
        >
          <Box sx={{ fontSize: 12, lineHeight: '16px', color: ANALYTICS.muted, mb: '4px' }}>{formatPeriod(trend.periods[active] ?? '', unit)}</Box>
          {trend.series.map((line) => (
            <Box key={line.feature.id} sx={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: 12, lineHeight: '20px', color: ANALYTICS.muted }}>
              <Box component="span" aria-hidden="true" sx={{ width: 12, height: 3, borderRadius: '9999px', bgcolor: line.color, flexShrink: 0 }} />
              <Box component="span" sx={{ fontSize: 14, fontWeight: 700, color: ANALYTICS.ink, fontVariantNumeric: 'tabular-nums' }}>
                {line.values[active] ?? 0}
              </Box>
              <Box component="span">{line.feature.name}</Box>
            </Box>
          ))}
        </Box>
      )}
    </Box>
  );
}
